import React, { useState, useRef, useEffect } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../services/firebase';
import { Song, UploadQueueItem, UploadStatus } from '../../types';
import authService from '../../services/authService';
import analyticsService from '../../services/analyticsService';
import audioAnalysisService from '../../services/audioAnalysisService';
import metadataExtractor from '../../services/metadataExtractor';

interface AudioUploaderProps {
  isVisible: boolean;
  onClose: () => void;
  onSongUploaded: (song: Song) => void;
  existingSongs: Song[];
}

export const AudioUploader: React.FC<AudioUploaderProps> = ({ 
  isVisible, 
  onClose,
  onSongUploaded,
  existingSongs
}) => {
  const [uploadQueue, setUploadQueue] = useState<UploadQueueItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [hasAgreedToRights, setHasAgreedToRights] = useState(false);
  
  const audioContextRef = useRef<AudioContext | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const processingRef = useRef(false);

  // Initialize Web Audio API context
  useEffect(() => {
    if (!audioContextRef.current) {
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      audioContextRef.current = new AudioContext();
    }
  }, []);

  // Process queue when items are added
  useEffect(() => {
    if (uploadQueue.length > 0 && !processingRef.current) {
      processQueue();
    }
  }, [uploadQueue]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (!authService.isAuthenticated() || !authService.getCurrentUser()) {
      setError('Please sign in to upload music');
      return;
    }

    setError('');
    
    // Create queue items for all selected files
    const newQueueItems: UploadQueueItem[] = Array.from(files).map(file => {
      // Validate file type
      if (!file.type.startsWith('audio/')) {
        analyticsService.trackError('audio_upload_invalid_type', file.type);
        return null;
      }

      return {
        id: `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        file,
        status: 'pending' as UploadStatus,
        progress: 0,
        songName: file.name.replace(/\.[^/.]+$/, '')
      };
    }).filter((item): item is UploadQueueItem => item !== null);

    if (newQueueItems.length === 0) {
      setError('No valid audio files selected');
      return;
    }

    setUploadQueue(prev => [...prev, ...newQueueItems]);
    analyticsService.trackFeatureUse('bulk_upload_started', `count:${newQueueItems.length}`);
  };

  const processQueue = async () => {
    if (processingRef.current || !audioContextRef.current) return;
    processingRef.current = true;
    setIsProcessing(true);

    const user = authService.getCurrentUser();
    if (!user) {
      processingRef.current = false;
      setIsProcessing(false);
      return;
    }

    // Process one item at a time
    for (let i = 0; i < uploadQueue.length; i++) {
      const item = uploadQueue[i];
      
      // Skip if already processed
      if (item.status !== 'pending') continue;

      try {
        // Update status to checking
        updateQueueItem(item.id, { status: 'checking' });

        // Decode audio to get duration
        const arrayBuffer = await item.file.arrayBuffer();
        const audioBuffer = await audioContextRef.current!.decodeAudioData(arrayBuffer.slice(0));
        const duration = audioBuffer.duration;

        updateQueueItem(item.id, { duration });

        // Check for duplicates
        const duplicate = existingSongs.find(song => 
          song.name === item.songName && Math.abs(song.duration - duration) < 1
        );

        if (duplicate) {
          updateQueueItem(item.id, { 
            status: 'duplicate', 
            error: 'Already exists in library' 
          });
          analyticsService.trackError('song_upload_duplicate', `name:${item.songName}|duration:${duration}`);
          continue;
        }

        // Start upload
        updateQueueItem(item.id, { status: 'uploading', progress: 0 });

        const songId = `song_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const storagePath = `users/${user.uid}/audio/${songId}/${item.file.name}`;
        const storageRef = ref(storage, storagePath);

        // Upload file
        const uploadTask = uploadBytesResumable(storageRef, item.file);

        await new Promise<void>((resolve, reject) => {
          uploadTask.on('state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              updateQueueItem(item.id, { progress });
            },
            (error) => {
              console.error('Upload error:', error);
              updateQueueItem(item.id, { 
                status: 'error', 
                error: 'Upload failed' 
              });
              analyticsService.trackError('song_upload_storage_failed', error.message);
              reject(error);
            },
            async () => {
              try {
                const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);

                // Extract metadata from file
                let metadata;
                try {
                  metadata = await metadataExtractor.extractMetadata(item.file);
                  analyticsService.trackFeatureUse('metadata_extraction_success', `hasArtist:${!!metadata.artist}`);
                } catch (metadataError) {
                  console.warn('Metadata extraction failed:', metadataError);
                  analyticsService.trackError('metadata_extraction_failed', String(metadataError));
                  metadata = {};
                }

                // Analyze audio for spectral metadata
                updateQueueItem(item.id, { status: 'analyzing', progress: 100 });
                let analysis;
                try {
                  analysis = await audioAnalysisService.analyzeAudioBuffer(audioBuffer);
                  analyticsService.trackFeatureUse('audio_analysis_success', `duration:${duration.toFixed(2)}s`);
                } catch (analysisError) {
                  console.warn('Audio analysis failed, continuing without metadata:', analysisError);
                  analyticsService.trackError('audio_analysis_failed', String(analysisError));
                  analysis = undefined;
                }

                const song: Song = {
                  id: songId,
                  name: metadata.title || item.songName,
                  fileName: item.file.name,
                  storagePath: storagePath,
                  downloadUrl: downloadUrl,
                  duration: duration,
                  size: item.file.size,
                  addedAt: Date.now(),
                  artist: metadata.artist,
                  album: metadata.album,
                  albumArtist: metadata.albumArtist,
                  genre: metadata.genre,
                  year: metadata.year,
                  trackNumber: metadata.trackNumber,
                  discNumber: metadata.discNumber,
                  composer: metadata.composer,
                  comment: metadata.comment,
                  analysis: analysis
                };

                onSongUploaded(song);
                updateQueueItem(item.id, { status: 'completed', progress: 100 });
                analyticsService.trackFeatureUse('song_upload_success', `duration:${duration.toFixed(2)}s|size:${(item.file.size / 1024 / 1024).toFixed(2)}MB`);
                resolve();
              } catch (error: any) {
                console.error('Failed to get download URL:', error);
                updateQueueItem(item.id, { 
                  status: 'error', 
                  error: 'Failed to complete upload' 
                });
                analyticsService.trackError('song_upload_url_failed', error?.message || 'Unknown error');
                reject(error);
              }
            }
          );
        }).catch(() => {
          // Error already handled in uploadTask.on
        });

      } catch (error: any) {
        console.error('Failed to process audio file:', error);
        updateQueueItem(item.id, { 
          status: 'error', 
          error: 'Failed to process file' 
        });
        analyticsService.trackError('audio_decode_failed', error?.message || 'Unknown error');
      }
    }

    processingRef.current = false;
    setIsProcessing(false);
  };

  const updateQueueItem = (id: string, updates: Partial<UploadQueueItem>) => {
    setUploadQueue(prev => prev.map(item => 
      item.id === id ? { ...item, ...updates } : item
    ));
  };

  const removeQueueItem = (id: string) => {
    setUploadQueue(prev => prev.filter(item => item.id !== id));
  };

  const clearCompleted = () => {
    setUploadQueue(prev => prev.filter(item => 
      item.status !== 'completed' && item.status !== 'duplicate'
    ));
  };

  const clearAll = () => {
    setUploadQueue([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    // Only allow closing if not processing
    if (!isProcessing) {
      clearAll();
      setError('');
      setHasAgreedToRights(false);
      onClose();
    }
  };

  const getStatusColor = (status: UploadStatus) => {
    switch (status) {
      case 'completed': return 'text-green-600';
      case 'duplicate': return 'text-yellow-600';
      case 'error': return 'text-red-600';
      case 'uploading': return 'text-blue-600';
      case 'analyzing': return 'text-indigo-600';
      case 'checking': return 'text-purple-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusIcon = (status: UploadStatus) => {
    switch (status) {
      case 'completed':
        return (
          <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'duplicate':
        return (
          <svg className="h-5 w-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        );
      case 'error':
        return (
          <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      case 'uploading':
        return (
          <svg className="animate-spin h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        );
      case 'checking':
        return (
          <svg className="animate-spin h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        );
      case 'analyzing':
        return (
          <svg className="animate-pulse h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
          </svg>
        );
      default:
        return (
          <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const getStatusText = (status: UploadStatus) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'checking': return 'Checking...';
      case 'duplicate': return 'Duplicate';
      case 'uploading': return 'Uploading';
      case 'analyzing': return 'Analyzing...';
      case 'completed': return 'Completed';
      case 'error': return 'Failed';
    }
  };

  if (!isVisible) return null;

  const hasQueueItems = uploadQueue.length > 0;
  const completedCount = uploadQueue.filter(item => item.status === 'completed').length;
  const duplicateCount = uploadQueue.filter(item => item.status === 'duplicate').length;
  const errorCount = uploadQueue.filter(item => item.status === 'error').length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Upload Music
            </h2>
            {hasQueueItems && (
              <p className="text-sm text-gray-500 mt-1">
                {uploadQueue.length} file{uploadQueue.length !== 1 ? 's' : ''} • 
                {completedCount > 0 && ` ${completedCount} completed`}
                {duplicateCount > 0 && ` • ${duplicateCount} duplicate${duplicateCount !== 1 ? 's' : ''}`}
                {errorCount > 0 && ` • ${errorCount} failed`}
              </p>
            )}
          </div>
          <button 
            onClick={handleClose}
            disabled={isProcessing}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Description */}
          <p className="text-sm text-gray-600">
            Upload one or multiple audio files to add them to your music library. Select multiple files to upload them in bulk.
          </p>

          {/* Rights Agreement */}
          <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={hasAgreedToRights}
                onChange={(e) => setHasAgreedToRights(e.target.checked)}
                className="mt-0.5 h-4 w-4 text-purple-600 border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-200">
                I confirm that I own the rights to upload this audio or have permission from the copyright holder to use it.
              </span>
            </label>
          </div>

          {/* File Upload */}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              onChange={handleFileSelect}
              className="hidden"
              disabled={isProcessing || !hasAgreedToRights}
              multiple
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isProcessing || !hasAgreedToRights}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span className="text-sm font-medium text-gray-700">
                Choose audio files (single or multiple)
              </span>
            </button>
          </div>

          {/* Upload Queue */}
          {hasQueueItems && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700">Upload Queue</h3>
                <div className="flex gap-2">
                  {(completedCount > 0 || duplicateCount > 0) && (
                    <button
                      onClick={clearCompleted}
                      disabled={isProcessing}
                      className="text-xs px-3 py-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Clear Completed
                    </button>
                  )}
                  {!isProcessing && (
                    <button
                      onClick={clearAll}
                      className="text-xs px-3 py-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                    >
                      Clear All
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {uploadQueue.map(item => (
                  <div 
                    key={item.id}
                    className="bg-gray-50 rounded-lg p-3 space-y-2"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        {getStatusIcon(item.status)}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {item.songName}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs font-medium ${getStatusColor(item.status)}`}>
                              {getStatusText(item.status)}
                            </span>
                            {item.duration && (
                              <span className="text-xs text-gray-500">
                                • {Math.floor(item.duration / 60)}:{(Math.floor(item.duration % 60)).toString().padStart(2, '0')}
                              </span>
                            )}
                            {item.status === 'uploading' && (
                              <span className="text-xs text-gray-500">
                                • {Math.round(item.progress)}%
                              </span>
                            )}
                          </div>
                          {item.error && (
                            <p className="text-xs text-red-600 mt-1">{item.error}</p>
                          )}
                        </div>
                      </div>
                      {!isProcessing && (item.status === 'completed' || item.status === 'duplicate' || item.status === 'error') && (
                        <button
                          onClick={() => removeQueueItem(item.id)}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>

                    {/* Progress Bar */}
                    {item.status === 'uploading' && (
                      <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                        <div
                          className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                          style={{ width: `${item.progress}%` }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              <strong>Tip:</strong> Your music will be stored securely in the cloud. Duplicate files (same name and duration) will be automatically skipped.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
